import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ShoppingCart, FileText, Package, BarChart3, Shield, 
  Smartphone, Cloud, Check, ArrowRight, Star, Users,
  Receipt, Scale, Printer, CreditCard, Zap, Clock,
  Store, MessageCircle, Truck, Globe, Lock, Headphones,
  ChevronRight, Play, Sparkles, TrendingUp, Award,
  CheckCircle2, Phone, Mail, MapPin, Instagram, Facebook,
  Youtube, Menu, X
} from 'lucide-react';
import { api } from '@/lib/api';
import { useToast } from '@/components/Toast';
import { LandingDepoimentos } from '@/components/landing/LandingBlocks';
import { PlanModulesList } from '@/components/PlanModulesList';
import { useRealTestimonials } from '@/lib/useRealTestimonials';

const API_URL = BACKEND_URL;

export const LandingPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [showCadastro, setShowCadastro] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [tipoPessoa, setTipoPessoa] = useState('cnpj'); // 'cnpj' ou 'cpf'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const depoimentosReais = useRealTestimonials();
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [formData, setFormData] = useState({
    razaoSocial: '',
    nomeFantasia: '',
    cnpj: '',
    cpf: '',
    email: '',
    telefone: '',
    nomeUsuario: '',
    emailUsuario: '',
    senha: '',
    confirmarSenha: ''
  });

  // Carregar planos do SaaS
  useEffect(() => {
    // 🆕 Persistir código do parceiro (se veio na URL) — assim mesmo se o
    // visitante navegar pra outra página antes de se cadastrar, mantemos.
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('partner_ref_code', ref.toUpperCase());
    }
    if (params.get('cadastro') === '1') setShowCadastro(true);

    const fetchPlans = async () => {
      try {
        const response = await fetch(`${API_URL}/api/saas/plans`);
        const data = await response.json();
        if (data.plans) {
          // Ordenar por preço
          const sortedPlans = data.plans.sort((a, b) => a.price - b.price);
          setPlans(sortedPlans);
        }
      } catch (error) {
        console.error('Erro ao carregar planos:', error);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  const handleCadastro = async (e) => {
    e.preventDefault();
    
    if (formData.senha !== formData.confirmarSenha) {
      toast.warning('As senhas não coincidem');
      return;
    }
    
    if (formData.senha.length < 6) {
      toast.warning('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      // 🆕 Capturar código de indicação do parceiro (?ref=CODIGO na URL)
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref') || localStorage.getItem('partner_ref_code') || null;

      const response = await api.post('/auth/register-company', {
        company: {
          razao_social: formData.razaoSocial,
          nome_fantasia: formData.nomeFantasia,
          cnpj: tipoPessoa === 'cnpj' ? formData.cnpj : formData.cpf,
          email: formData.email,
          telefone: formData.telefone
        },
        user: {
          name: formData.nomeUsuario,
          email: formData.emailUsuario,
          password: formData.senha
        },
        ref_code: refCode,
      });

      // Limpar código após sucesso
      if (refCode) localStorage.removeItem('partner_ref_code');
      
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Cadastro realizado com sucesso! Bem-vindo ao nfcesysPdv!');
        window.location.href = '/dashboard';
      } else {
        toast.success('Cadastro realizado! Faça login para continuar.');
        navigate('/login');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar cadastro');
    } finally {
      setLoading(false);
    }
  };

  // Função para consultar CNPJ
  const consultarCNPJ = async () => {
    const cnpjLimpo = formData.cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      toast.warning('Digite um CNPJ válido com 14 dígitos');
      return;
    }
    
    setConsultando(true);
    try {
      const response = await api.get(`/consulta/cnpj/${cnpjLimpo}`);
      if (response.data.success) {
        const dados = response.data.data;
        setFormData(prev => ({
          ...prev,
          razaoSocial: dados.razao_social || '',
          nomeFantasia: dados.nome_fantasia || '',
          telefone: dados.telefone || '',
          email: dados.email || prev.email
        }));
        toast.success('Dados carregados com sucesso!');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao consultar CNPJ';
      toast.error(msg);
    } finally {
      setConsultando(false);
    }
  };

  // Função para consultar CPF
  const consultarCPF = async () => {
    const cpfLimpo = formData.cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      toast.warning('Digite um CPF válido com 11 dígitos');
      return;
    }
    
    setConsultando(true);
    try {
      const response = await api.get(`/consulta/cpf/${cpfLimpo}`);
      if (response.data.success) {
        toast.success('CPF válido! Preencha os demais dados.');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'CPF inválido';
      toast.error(msg);
    } finally {
      setConsultando(false);
    }
  };

  // Formatar CNPJ enquanto digita
  const formatarCNPJ = (valor) => {
    const numeros = valor.replace(/\D/g, '').slice(0, 14);
    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 5) return `${numeros.slice(0,2)}.${numeros.slice(2)}`;
    if (numeros.length <= 8) return `${numeros.slice(0,2)}.${numeros.slice(2,5)}.${numeros.slice(5)}`;
    if (numeros.length <= 12) return `${numeros.slice(0,2)}.${numeros.slice(2,5)}.${numeros.slice(5,8)}/${numeros.slice(8)}`;
    return `${numeros.slice(0,2)}.${numeros.slice(2,5)}.${numeros.slice(5,8)}/${numeros.slice(8,12)}-${numeros.slice(12)}`;
  };

  // Formatar CPF enquanto digita
  const formatarCPF = (valor) => {
    const numeros = valor.replace(/\D/g, '').slice(0, 11);
    if (numeros.length <= 3) return numeros;
    if (numeros.length <= 6) return `${numeros.slice(0,3)}.${numeros.slice(3)}`;
    if (numeros.length <= 9) return `${numeros.slice(0,3)}.${numeros.slice(3,6)}.${numeros.slice(6)}`;
    return `${numeros.slice(0,3)}.${numeros.slice(3,6)}.${numeros.slice(6,9)}-${numeros.slice(9)}`;
  };

  // Todas as funcionalidades do sistema
  const allFeatures = [
    { icon: Receipt, title: 'Emissão de NFC-e', desc: 'Cupom fiscal eletrônico homologado pela SEFAZ em todos os estados', color: 'from-amber-500 to-orange-600' },
    { icon: ShoppingCart, title: 'PDV Completo', desc: 'Ponto de venda moderno, rápido e intuitivo com atalhos de teclado', color: 'from-blue-500 to-cyan-600' },
    { icon: Package, title: 'Controle de Estoque', desc: 'Gerencie entradas, saídas e inventário em tempo real', color: 'from-emerald-500 to-green-600' },
    { icon: Scale, title: 'Integração Balança', desc: 'Compatível com Toledo, Filizola, Urano e outras marcas', color: 'from-purple-500 to-violet-600' },
    { icon: CreditCard, title: 'Múltiplos Pagamentos', desc: 'PIX, cartão de crédito/débito, dinheiro, voucher e fiado', color: 'from-pink-500 to-rose-600' },
    { icon: BarChart3, title: 'Relatórios Completos', desc: 'Análises de vendas, estoque, financeiro e fiscal', color: 'from-indigo-500 to-blue-600' },
    { icon: Store, title: 'Marketplaces', desc: 'Integração com iFood, Rappi, Zé Delivery, Mercado Livre e Shopee', color: 'from-red-500 to-orange-600' },
    { icon: MessageCircle, title: 'WhatsApp Bot', desc: 'Atendimento automático inteligente pelo WhatsApp', color: 'from-green-500 to-emerald-600' },
    { icon: Users, title: 'Multi-usuários', desc: 'Controle de acesso por perfil: admin, gerente e caixa', color: 'from-cyan-500 to-blue-600' },
    { icon: Printer, title: 'Impressão Térmica', desc: 'Cupom em 80mm, etiquetas de gôndola e relatórios', color: 'from-slate-500 to-gray-600' },
    { icon: Globe, title: 'Catálogo Online', desc: 'Loja virtual com seu domínio para pedidos online', color: 'from-teal-500 to-cyan-600' },
    { icon: Lock, title: 'Controle de Caixa', desc: 'Abertura, fechamento, sangria e suprimento com relatórios', color: 'from-yellow-500 to-amber-600' },
  ];

  // Diferenciais
  const differentials = [
    { icon: Cloud, title: '100% na Nuvem', desc: 'Acesse de qualquer lugar, computador ou celular' },
    { icon: Shield, title: 'Dados Seguros', desc: 'Backup automático diário e criptografia' },
    { icon: Zap, title: 'Super Rápido', desc: 'Sistema leve que funciona mesmo com internet lenta' },
    { icon: Headphones, title: 'Suporte Humano', desc: 'Atendimento por WhatsApp, chat e telefone' },
  ];

  // Números
  const stats = [
    { value: '10.000+', label: 'Empresas Ativas' },
    { value: '5M+', label: 'NFC-e Emitidas' },
    { value: '99.9%', label: 'Uptime Garantido' },
    { value: '4.9★', label: 'Avaliação Clientes' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 overflow-x-hidden">
      {/* Header Fixo */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                  <ShoppingCart className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-slate-950 flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div>
                <span className="text-xl font-bold text-white tracking-tight">nfcesysPdv</span>
                <span className="hidden sm:block text-[10px] text-slate-500 -mt-1">Sistema PDV com NFC-e</span>
              </div>
            </div>

            {/* Nav Desktop */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#funcionalidades" className="text-sm text-slate-400 hover:text-white transition-colors">Funcionalidades</a>
              <a href="#planos" className="text-sm text-slate-400 hover:text-white transition-colors">Planos</a>
              <a href="/blog" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="nav-blog-link">Blog</a>
              <a href="#contato" className="text-sm text-slate-400 hover:text-white transition-colors">Contato</a>
            </nav>

            {/* CTAs */}
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="hidden sm:flex text-slate-300 hover:text-white hover:bg-slate-800"
                onClick={() => navigate('/login')}
              >
                Entrar
              </Button>
              <Button 
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/25 border-0"
                onClick={() => setShowCadastro(true)}
              >
                <span className="hidden sm:inline">Teste Grátis</span>
                <span className="sm:hidden">Começar</span>
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              
              {/* Mobile Menu Button */}
              <Button 
                variant="ghost" 
                size="icon"
                className="md:hidden text-slate-400"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-slate-800 pt-4">
              <nav className="flex flex-col gap-3">
                <a href="#funcionalidades" className="text-slate-300 hover:text-white py-2">Funcionalidades</a>
                <a href="#planos" className="text-slate-300 hover:text-white py-2">Planos</a>
                <a href="/blog" className="text-slate-300 hover:text-white py-2" data-testid="mobile-nav-blog-link">Blog</a>
                <a href="#contato" className="text-slate-300 hover:text-white py-2">Contato</a>
                <Button variant="outline" className="mt-2" onClick={() => navigate('/login')}>Entrar</Button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-36 pb-20 px-4 sm:px-6 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-amber-500/5 to-transparent rounded-full" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-400 px-4 py-2 rounded-full text-sm mb-8 backdrop-blur-sm">
              <Sparkles className="w-4 h-4" />
              <span>Sistema PDV completo para varejo e food service</span>
              <ChevronRight className="w-4 h-4" />
            </div>
            
            {/* Título Principal - H1 para SEO */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-[1.1] tracking-tight">
              Emita{' '}
              <span className="relative">
                <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                  Cupom Fiscal
                </span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <path d="M2 10C50 4 150 2 298 8" stroke="url(#gradient)" strokeWidth="3" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="300" y2="0">
                      <stop stopColor="#F59E0B"/>
                      <stop offset="1" stopColor="#EF4444"/>
                    </linearGradient>
                  </defs>
                </svg>
              </span>
              <br />
              em segundos
            </h1>
            
            {/* Subtítulo - H2 para SEO */}
            <h2 className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Sistema de <strong className="text-white">PDV</strong> com emissão de <strong className="text-white">NFC-e</strong>, 
              controle de <strong className="text-white">estoque</strong>, integração com <strong className="text-white">balanças</strong>, 
              <strong className="text-white"> marketplaces</strong> e muito mais. 100% na nuvem.
            </h2>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-lg px-8 py-7 rounded-xl shadow-2xl shadow-amber-500/30 border-0 group"
                onClick={() => setShowCadastro(true)}
              >
                <Zap className="w-5 h-5 mr-2 group-hover:animate-pulse" />
                Começar Grátis - 30 Dias
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800/50 hover:border-slate-600 text-lg px-8 py-7 rounded-xl backdrop-blur-sm"
                onClick={() => navigate('/login')}
              >
                <Play className="w-5 h-5 mr-2" />
                Ver Demonstração
              </Button>
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Sem cartão de crédito</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Cancele quando quiser</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Suporte incluso</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span>Homologado SEFAZ</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 text-center hover:border-amber-500/50 transition-colors">
                <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Funcionalidades - Seção Principal para SEO */}
      <section id="funcionalidades" className="py-20 px-4 sm:px-6 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
              +12 Funcionalidades
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Tudo que seu negócio precisa
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Sistema completo de gestão comercial com emissão de nota fiscal eletrônica NFC-e
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {allFeatures.map((feature, index) => (
              <Card 
                key={index} 
                className="group bg-slate-900/50 border-slate-800 hover:border-amber-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-amber-500/10 overflow-hidden"
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-amber-400 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-4">
                Por que escolher o nfcesysPdv?
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Sistema PDV profissional para seu negócio crescer
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                Desenvolvido por quem entende de varejo. Mais de 10 anos de experiência 
                em sistemas de gestão comercial e emissão de documentos fiscais.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {differentials.map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-3xl blur-3xl" />
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 border border-slate-700">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm mb-4">
                    <Zap className="w-4 h-4" />
                    Oferta Especial
                  </div>
                  <div className="text-7xl font-bold text-white mb-2">30</div>
                  <div className="text-xl text-slate-400 mb-6">dias grátis para testar</div>
                  <p className="text-slate-500 mb-6">
                    Experimente todas as funcionalidades sem compromisso. 
                    Sem precisar de cartão de crédito.
                  </p>
                  <Button 
                    size="lg"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-xl py-6 text-lg"
                    onClick={() => setShowCadastro(true)}
                  >
                    Criar Conta Grátis
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planos Dinâmicos */}
      <section id="planos" className="py-20 px-4 sm:px-6 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 mb-4">
              Planos Flexíveis
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Escolha o plano ideal para você
            </h2>
            <p className="text-lg text-slate-400">
              Planos que cabem no bolso do pequeno ao grande empresário
            </p>
          </div>
          
          {loadingPlans ? (
            <div className="flex justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan, index) => {
                const isPopular = plan.name?.toLowerCase().includes('profissional') || index === 1;
                
                return (
                  <Card 
                    key={plan.id || index} 
                    className={`relative bg-slate-900/80 border-slate-800 overflow-hidden transition-all duration-300 hover:-translate-y-2 ${
                      isPopular ? 'ring-2 ring-amber-500 shadow-xl shadow-amber-500/20' : 'hover:border-slate-700'
                    }`}
                  >
                    {isPopular && (
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm py-2 text-center font-medium">
                        <Star className="w-4 h-4 inline mr-1" />
                        Mais Popular
                      </div>
                    )}
                    <CardContent className={`p-6 ${isPopular ? 'pt-12' : ''}`}>
                      <h3 className="text-xl font-bold text-white mb-2">{plan.name || 'Plano'}</h3>
                      <p className="text-sm text-slate-400 mb-4">{plan.description || ''}</p>
                      
                      <div className="mb-6">
                        <span className="text-4xl font-bold text-white">
                          R$ {(plan.price || 0).toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-slate-400">/mês</span>
                      </div>
                      
                      <div className="mb-6">
                        <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Módulos liberados</p>
                        <PlanModulesList plan={plan} dark />
                      </div>
                      
                      <Button 
                        className={`w-full rounded-xl py-5 ${
                          isPopular 
                            ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white' 
                            : 'bg-slate-800 hover:bg-slate-700 text-white'
                        }`}
                        onClick={() => setShowCadastro(true)}
                      >
                        Começar Agora
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          
          <p className="text-center text-slate-500 mt-8 text-sm">
            Todos os planos incluem 30 dias grátis. Cancele a qualquer momento.
          </p>
        </div>
      </section>

      {depoimentosReais.length > 0 && (
        <LandingDepoimentos depoimentos={depoimentosReais} logos={depoimentosReais.map((r) => ({ nome: r.negocio, logo: r.logo }))} real />
      )}

      {/* CTA Final */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 p-8 sm:p-12">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }} />
            </div>
            <div className="relative text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Pronto para modernizar seu negócio?
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                Junte-se a mais de 10.000 empresas que já emitem cupom fiscal com o nfcesysPdv.
                Comece agora e ganhe 30 dias grátis!
              </p>
              <Button 
                size="lg"
                className="bg-white text-orange-600 hover:bg-slate-100 text-lg px-8 py-6 rounded-xl shadow-xl"
                onClick={() => setShowCadastro(true)}
              >
                <Zap className="w-5 h-5 mr-2" />
                Criar Conta Grátis
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer com SEO */}
      <footer id="contato" className="py-16 px-4 sm:px-6 border-t border-slate-800 bg-slate-950" role="contentinfo" itemScope itemType="https://schema.org/Organization">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 mb-12">
            {/* Logo e Descrição */}
            <div className="col-span-2 lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white" itemProp="name">nfcesysPdv</span>
              </div>
              <p className="text-slate-400 text-sm mb-4 max-w-xs" itemProp="description">
                Sistema de PDV completo com emissão de cupom fiscal eletrônico NFC-e, 
                controle de estoque e gestão comercial na nuvem. Software para loja, mercado, 
                restaurante e food service.
              </p>
              <div className="flex gap-3">
                <a href="https://www.instagram.com/nfcesys/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center text-slate-400 hover:text-white transition-colors" aria-label="Instagram nfcesys" itemProp="sameAs">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="https://www.facebook.com/nfcesys" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center text-slate-400 hover:text-white transition-colors" aria-label="Facebook nfcesys" itemProp="sameAs">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="https://www.youtube.com/@nfcesysmaxtecomp5701" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center text-slate-400 hover:text-white transition-colors" aria-label="YouTube nfcesys" itemProp="sameAs">
                  <Youtube className="w-4 h-4" />
                </a>
                <a href="https://x.com/nfcesys" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 hover:bg-slate-700 rounded-lg flex items-center justify-center text-slate-400 hover:text-white transition-colors" aria-label="X (Twitter) nfcesys" itemProp="sameAs">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
              </div>
            </div>
            
            {/* Produto */}
            <nav aria-label="Produto">
              <h4 className="text-white font-semibold mb-4">Produto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#funcionalidades" className="text-slate-400 hover:text-white transition-colors">Funcionalidades do PDV</a></li>
                <li><a href="#planos" className="text-slate-400 hover:text-white transition-colors">Planos e Preços</a></li>
                <li><a href="/blog" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-blog-link">Blog & Artigos</a></li>
                <li><a href="/pdv-para-acougue" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-seg-acougue">PDV para Açougue</a></li>
                <li><a href="/pdv-para-mercado" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-seg-mercado">PDV para Mercado</a></li>
                <li><a href="/pdv-para-restaurante" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-seg-restaurante">PDV para Restaurante</a></li>
                <li><a href="/pdv-para-padaria" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-seg-padaria">PDV para Padaria</a></li>
                <li><a href="/pdv-para-lanchonete" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-seg-lanchonete">PDV para Lanchonete</a></li>
                <li><a href="/pdv-para-loja-de-roupas" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-seg-roupas">PDV para Loja de Roupas</a></li>
                <li><a href="/sistema-pdv-sao-paulo" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-city-sp">Sistema PDV em São Paulo</a></li>
                <li><a href="/sistema-pdv-belo-horizonte" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-city-bh">Sistema PDV em Belo Horizonte</a></li>
                <li><a href="/sistema-pdv-curitiba" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-city-cwb">Sistema PDV em Curitiba</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Emissor de NFC-e</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Controle de Estoque</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Integração com Balanças</a></li>
              </ul>
            </nav>
            
            {/* Recursos */}
            <nav aria-label="Recursos">
              <h4 className="text-white font-semibold mb-4">Recursos</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Frente de Caixa PDV</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Relatórios de Vendas</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Integração Marketplaces</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">WhatsApp Bot Vendas</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Catálogo Online</a></li>
              </ul>
            </nav>
            
            {/* Contato */}
            <address className="not-italic" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
              <h4 className="text-white font-semibold mb-4">Contato</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2 text-slate-400">
                  <Phone className="w-4 h-4 text-amber-500" />
                  <a href="tel:+5511947289661" itemProp="telephone">(11) 94728-9661</a>
                </li>
                <li className="flex items-center gap-2 text-slate-400">
                  <Mail className="w-4 h-4 text-amber-500" />
                  <a href="mailto:contato@nfcesys.com.br" itemProp="email">contato@nfcesys.com.br</a>
                </li>
                <li className="flex items-start gap-2 text-slate-400">
                  <MapPin className="w-4 h-4 text-amber-500 mt-0.5" />
                  <span>
                    <span itemProp="streetAddress">Rua Rio Bonito, 637 - Brás</span>, 
                    <span itemProp="addressLocality">São Paulo</span>/<span itemProp="addressRegion">SP</span>
                  </span>
                </li>
              </ul>
            </address>
          </div>
          
          {/* Bottom Footer - SEO Keywords Rich */}
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-500 text-sm">
                © {new Date().getFullYear()} nfcesysPdv. Todos os direitos reservados.
              </p>
              <nav aria-label="Palavras-chave SEO" className="text-slate-600 text-xs text-center">
                <span>Sistema PDV</span> | <span>Emissor NFC-e</span> | <span>Cupom Fiscal Eletrônico</span> | 
                <span>Nota Fiscal Consumidor</span> | <span>Controle de Estoque</span> | 
                <span>Software para Loja</span> | <span>Frente de Caixa</span> | <span>ERP Varejo</span> | 
                <span>Balança Toledo</span> | <span>Balança Filizola</span> | <span>PDV Online</span> | 
                <span>Sistema Comercial</span>
              </nav>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal de Cadastro */}
      {showCadastro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowCadastro(false)} />
          <div className="relative bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-900 p-6 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Criar Conta Grátis</h2>
                <p className="text-slate-400">Teste por 30 dias sem compromisso</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowCadastro(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <form onSubmit={handleCadastro} className="p-6 space-y-6">
              {/* Dados da Empresa */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-500" />
                  Dados da Empresa
                </h3>
                
                {/* Seletor CPF/CNPJ */}
                <div className="flex gap-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setTipoPessoa('cnpj')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      tipoPessoa === 'cnpj' 
                        ? 'border-amber-500 bg-amber-500/10 text-amber-400' 
                        : 'border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    <span className="font-semibold">Pessoa Jurídica</span>
                    <p className="text-xs mt-1 opacity-70">CNPJ</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTipoPessoa('cpf')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      tipoPessoa === 'cpf' 
                        ? 'border-amber-500 bg-amber-500/10 text-amber-400' 
                        : 'border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    <span className="font-semibold">Pessoa Física</span>
                    <p className="text-xs mt-1 opacity-70">CPF (MEI/Autônomo)</p>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Campo CNPJ ou CPF com botão de consulta */}
                  <div className="col-span-2">
                    <Label className="text-slate-300">{tipoPessoa === 'cnpj' ? 'CNPJ *' : 'CPF *'}</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={tipoPessoa === 'cnpj' ? formData.cnpj : formData.cpf}
                        onChange={(e) => {
                          if (tipoPessoa === 'cnpj') {
                            setFormData({...formData, cnpj: formatarCNPJ(e.target.value)});
                          } else {
                            setFormData({...formData, cpf: formatarCPF(e.target.value)});
                          }
                        }}
                        placeholder={tipoPessoa === 'cnpj' ? '00.000.000/0000-00' : '000.000.000-00'}
                        required
                        className="flex-1 bg-slate-800 border-slate-700 text-white"
                      />
                      <Button
                        type="button"
                        onClick={tipoPessoa === 'cnpj' ? consultarCNPJ : consultarCPF}
                        disabled={consultando}
                        className="bg-amber-500 hover:bg-amber-600 text-white px-4"
                      >
                        {consultando ? (
                          <span className="animate-spin">⏳</span>
                        ) : (
                          'Consultar'
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {tipoPessoa === 'cnpj' 
                        ? 'Clique em Consultar para preencher automaticamente' 
                        : 'Clique em Consultar para validar o CPF'}
                    </p>
                  </div>
                  
                  <div className="col-span-2">
                    <Label className="text-slate-300">Razão Social / Nome *</Label>
                    <Input
                      value={formData.razaoSocial}
                      onChange={(e) => setFormData({...formData, razaoSocial: e.target.value})}
                      placeholder={tipoPessoa === 'cnpj' ? 'Razão Social da Empresa LTDA' : 'Nome Completo'}
                      required
                      className="mt-1 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Nome Fantasia</Label>
                    <Input
                      value={formData.nomeFantasia}
                      onChange={(e) => setFormData({...formData, nomeFantasia: e.target.value})}
                      placeholder="Nome Fantasia"
                      className="mt-1 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Email da Empresa *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="contato@empresa.com"
                      required
                      className="mt-1 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Telefone</Label>
                    <Input
                      value={formData.telefone}
                      onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                      placeholder="(00) 00000-0000"
                      className="mt-1 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Dados do Usuário */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  Dados do Administrador
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-300">Nome Completo *</Label>
                    <Input
                      value={formData.nomeUsuario}
                      onChange={(e) => setFormData({...formData, nomeUsuario: e.target.value})}
                      placeholder="Seu nome completo"
                      required
                      className="mt-1 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-300">Email de Acesso *</Label>
                    <Input
                      type="email"
                      value={formData.emailUsuario}
                      onChange={(e) => setFormData({...formData, emailUsuario: e.target.value})}
                      placeholder="seu@email.com"
                      required
                      className="mt-1 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Senha *</Label>
                    <Input
                      type="password"
                      value={formData.senha}
                      onChange={(e) => setFormData({...formData, senha: e.target.value})}
                      placeholder="Mínimo 6 caracteres"
                      required
                      className="mt-1 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Confirmar Senha *</Label>
                    <Input
                      type="password"
                      value={formData.confirmarSenha}
                      onChange={(e) => setFormData({...formData, confirmarSenha: e.target.value})}
                      placeholder="Repita a senha"
                      required
                      className="mt-1 bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                  onClick={() => setShowCadastro(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  disabled={loading}
                >
                  {loading ? 'Criando conta...' : 'Criar Conta Grátis'}
                </Button>
              </div>
              
              <p className="text-center text-sm text-slate-500">
                Ao criar sua conta, você concorda com nossos Termos de Uso e Política de Privacidade
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
